import { Controller } from "stimulus";
import { jitter } from "live_chat/backoff";
import { whenPlaybackStops } from "utils/live_playback";

// Spread over which the audience waiting on a not-yet-open room reloads once it opens.
const ROOM_OPEN_RELOAD_SPREAD_MS = 30000;
// Spread over which the audience of a finished event reloads into the next one.
const HANDOFF_SPREAD_MS = 30000;
// The server renders its timestamps in whole seconds, so a handoff that went at the exact moment
// could reach it a fraction early, resolve the same event, and reload straight back.
const HANDOFF_MARGIN_MS = 2000;
// Spread over which viewers still playing a finished premiere reload once they stop it.
const PLAYBACK_STOP_SPREAD_MS = 5000;

// Waiting-room countdown -> reveal a "chat closes in" countdown once the content finishes;
// transition the room to its closed state in place (no navigation) once a premiere's or stream's
// room closes - the video keeps playing to the end, the viewer is already entitled to the VOD.
// Shared by both live streams and premieres now. endsAtMs/playbackEndsAtMs are real for either
// kind: a stream's chat runs on for its own afterparty past the moment the creator ended it.
// At T-0 the two kinds diverge (see renderCountdown): a premiere is clock-driven, so reaching
// the target reloads into the playing state; a stream is webhook-driven, so reaching starts_at
// says nothing about whether the creator has actually gone live - kindValue picks the branch.
// waitingValue is the ticking-countdown gate, not the same thing as the page's own "waiting room"
// content state: a deferred premiere or a stream stuck past its own starts_at also render
// waiting-room copy, but with no live target to count down to, so the view leaves waitingValue
// false for those - see HeroComponent#countdown_target_ms.
// /live resolves the event itself, so once this event's content has finished the page hands off
// to the next one by reloading - see watchHandoff.
export default class ShowController extends Controller {
  static targets = ["countdown", "countdownLabel", "countdownOverdue", "chatClose", "chatCloseBar", "nextUp", "refreshStream"];
  static values = {
    kind: String,
    startsAtMs: Number,
    endsAtMs: Number,
    serverNowMs: Number,
    playbackEndsAtMs: Number,
    waiting: Boolean,
    // Seeded only when the page rendered before its chat room opened, and only with a future moment.
    roomOpensAtMs: Number,
    // When the next event's pre-show opens; seeded only while there is one.
    nextOpensAtMs: Number,
  };

  connect() {
    // Offset a skewed client clock onto server time (minted at render).
    this.clockOffset = this.serverNowMsValue - Date.now();

    if (this.waitingValue) {
      this.renderCountdown();
      this.countdownInterval = window.setInterval(() => this.renderCountdown(), 1000);
    }

    this.watchAfterparty();
    this.watchRoomClose();
    this.watchRoomOpen();
    this.watchHandoff();
  }

  disconnect() {
    if (this.countdownInterval) window.clearInterval(this.countdownInterval);
    if (this.windowEndInterval) window.clearInterval(this.windowEndInterval);
    if (this.afterpartyInterval) window.clearInterval(this.afterpartyInterval);
    if (this.closeRoomFrame) window.cancelAnimationFrame(this.closeRoomFrame);
    if (this.roomOpenInterval) window.clearInterval(this.roomOpenInterval);
    if (this.handoffInterval) window.clearInterval(this.handoffInterval);
    if (this.handoffTimer) window.clearTimeout(this.handoffTimer);
    if (this.cancelPlaybackWait) this.cancelPlaybackWait();
  }

  now() {
    return Date.now() + this.clockOffset;
  }

  renderCountdown() {
    const remaining = this.startsAtMsValue - this.now();
    if (remaining <= 0) {
      if (this.countdownInterval) window.clearInterval(this.countdownInterval);

      if (this.kindValue === "premiere") {
        // Clock-driven: reaching the target time IS the state change, so reload into it
        // (fresh signed URL + player).
        window.location.reload();
        return;
      }

      // Webhook-driven: reaching starts_at does not mean the creator has actually gone live
      // yet - mark_live! re-renders this page whenever it really happens (the chat room's
      // stream_started event, or the live_stream_settings stream while the room is not open
      // yet). Swap to the same static "Starting soon" markup a fresh
      // load in this state would render, rather than reload-looping on a stale target.
      if (this.hasCountdownLabelTarget) this.countdownLabelTarget.classList.add("hidden");
      if (this.hasCountdownTarget) this.countdownTarget.classList.add("hidden");
      if (this.hasCountdownOverdueTarget) this.countdownOverdueTarget.classList.remove("hidden");
      return;
    }
    if (this.hasCountdownTarget) this.countdownTarget.textContent = this.format(remaining);
  }

  // Reveal the "chat closes in M:SS" line and count it down to the afterparty end - both when the page
  // loads mid-afterparty and when a premiere's video finishes while the viewer is watching (no reload).
  watchAfterparty() {
    if (!this.playbackEndsAtMsValue || !this.hasChatCloseBarTarget) return;

    const tick = () => {
      if (this.now() < this.playbackEndsAtMsValue) return;
      this.chatCloseBarTarget.classList.remove("hidden");
      if (this.hasChatCloseTarget) {
        this.chatCloseTarget.textContent = this.format(Math.max(0, this.endsAtMsValue - this.now()));
      }
    };

    tick();
    this.afterpartyInterval = window.setInterval(tick, 1000);
  }

  // Room close can be hours out; setTimeout is unreliable over long spans/sleep, so poll the offset clock.
  watchRoomClose() {
    if (!this.endsAtMsValue) return;

    if (this.now() >= this.endsAtMsValue) {
      // Deferred one rAF tick, not called inline: this controller is an ancestor of the chat
      // panel, so Stimulus connects it first - dispatching synchronously here fires before the
      // panel's window listener exists. One frame is always enough because Stimulus finishes
      // connecting the whole tree, descendants' data-actions included, within the current
      // task/mutation-observer checkpoint.
      this.closeRoomFrame = window.requestAnimationFrame(() => this.closeRoom());
      return;
    }

    this.windowEndInterval = window.setInterval(() => {
      if (this.now() >= this.endsAtMsValue) {
        window.clearInterval(this.windowEndInterval);
        this.closeRoom();
      }
    }, 1000);
  }

  // Nothing re-renders a page when its room opens (chat was refused, and the later go-live refresh keeps the
  // turbo-permanent chat panel's pre-open markup), so reload once it opens: a full reload. No loop is possible
  // because the server seeds only a future moment.
  watchRoomOpen() {
    if (!this.roomOpensAtMsValue) return;

    const reloadAtMs = this.roomOpensAtMsValue + Math.random() * ROOM_OPEN_RELOAD_SPREAD_MS;
    this.roomOpenInterval = window.setInterval(() => {
      if (this.now() < reloadAtMs) return;
      window.clearInterval(this.roomOpenInterval);
      window.location.reload();
    }, 1000);
  }

  // Once the content has finished, reveal "Next up" and reload into the next event: as soon as its
  // pre-show is open (right away if it already is), spread across the audience. A live stream has
  // no playbackEndsAtMs yet; its stream_ended refresh re-renders the page with one. Polled rather
  // than timed out, since the next pre-show can be hours away and setTimeout is unreliable across sleep.
  watchHandoff() {
    if (!this.nextOpensAtMsValue) return;

    const spreadMs = jitter(0, HANDOFF_SPREAD_MS);
    const tick = () => {
      if (!this.playbackEndsAtMsValue || this.now() < this.playbackEndsAtMsValue) return;

      if (this.hasNextUpTarget) this.nextUpTarget.classList.remove("hidden");
      if (this.now() < Math.max(this.playbackEndsAtMsValue, this.nextOpensAtMsValue) + HANDOFF_MARGIN_MS + spreadMs) return;

      window.clearInterval(this.handoffInterval);
      this.handoffInterval = null;
      this.handOffWhenStopped();
    };

    this.handoffInterval = window.setInterval(tick, 1000);
    tick();
  }

  // Never refresh a viewer away from a video still playing: a premiere viewer who rewound watches it
  // to the end (or pauses) first, and a native-shell viewer's bridge player keeps the page for good.
  handOffWhenStopped() {
    this.cancelPlaybackWait = whenPlaybackStops(({ waited }) => {
      this.cancelPlaybackWait = null;
      this.handOff(waited ? jitter(0, PLAYBACK_STOP_SPREAD_MS) : 0);
    });
  }

  handOff(delayMs) {
    if (this.handedOff) return;

    this.handedOff = true;
    this.handoffTimer = window.setTimeout(() => window.location.reload(), delayMs);
  }

  // In-place transition once the room closes - no redirect, the video keeps playing where it is.
  // The chat panel's own "Chat is closed." notice is the one place that says so; this bar just
  // stops counting down rather than restating it.
  // The page then holds neither a room nor, having rendered with one open, a cable stream: it
  // subscribes to the refresh stream now, so what happens after the afterparty still reaches it.
  closeRoom() {
    if (this.afterpartyInterval) window.clearInterval(this.afterpartyInterval);
    if (this.hasChatCloseBarTarget) this.chatCloseBarTarget.classList.add("hidden");
    this.dispatch("room-closed", { target: window });
    if (this.hasRefreshStreamTarget) this.element.append(this.refreshStreamTarget.content.cloneNode(true));
  }

  // MM:SS, widening to H:MM:SS once an hour or more remains, and to "2d 03:04:05" for an event days out.
  format(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
    if (hours > 0) return `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(value) {
    return String(value).padStart(2, "0");
  }
}
